import { useEffect, useRef, useState } from 'react';
import { addCapture } from '../lib/actions.js';
import { VoiceRecorder, recordingSupported, tone } from '../lib/audio.js';
import { BigButton, Sheet } from '../components/Controls.js';

/**
 * Debriefing the job just left.
 *
 * The fifteen minutes between two stops is the only uninterrupted thinking time in a
 * technician's day, and it is exactly when the last job is freshest. It is also the moment
 * they legally cannot touch the phone: Quebec's Highway Safety Code prohibits handling a
 * device while driving, and hands-free is the only lawful form of this feature.
 *
 * That constraint is the design rather than an obstacle to it:
 *
 *   - **Armed before moving.** One large control, pressed while stationary. The recorder is
 *     then live for the drive.
 *   - **Nothing to look at and nothing to tap.** No confirmation screen, no waveform, no "are
 *     you still there". A tone on start and a tone on stop, and that is the whole interface.
 *   - **It attaches to the stop just left**, not the one being driven to, because that is what
 *     a debrief is about.
 *   - **Reviewed on arrival, not while moving.** The transcript is shown when the vehicle is
 *     stationary again, so a mis-heard part number is caught by the person who said it rather
 *     than by whoever reads it next week.
 *
 * Two honest caveats, both from `FIELD.md`. **The default is the curb, not the road**: sixty
 * seconds before pulling out is offered first, and the drive-time recorder is the bonus for
 * phones that handle it. And background capture is where a PWA is weakest — Chrome on Android
 * may suspend it when the screen locks and iOS will — so the recorder holds a dimmed wake
 * lock and says plainly when the platform refused one, rather than discovering it in a van.
 */
export function DebriefSheet({
  open,
  stopId,
  customerName,
  onClose,
}: {
  open: boolean;
  stopId: string;
  customerName: string;
  onClose: () => void;
}) {
  const recorder = useRef<VoiceRecorder | null>(null);
  const [mode, setMode] = useState<'idle' | 'curb' | 'drive'>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [unreliable, setUnreliable] = useState(false);

  useEffect(() => {
    if (mode === 'idle') return;
    const timer = window.setInterval(() => setElapsed((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [mode]);

  // Leaving the sheet must not leave a microphone open in somebody's pocket.
  useEffect(() => {
    if (open) return;
    void recorder.current?.cancel();
    recorder.current = null;
    setMode('idle');
  }, [open]);

  const start = async (which: 'curb' | 'drive') => {
    recorder.current = new VoiceRecorder();
    try {
      await recorder.current.start();
    } catch {
      setMode('idle');
      return;
    }
    setUnreliable(which === 'drive' && !recorder.current.wakeLockHeld);
    setElapsed(0);
    setMode(which);
    tone('start');
  };

  const stop = async () => {
    const result = await recorder.current?.stop();
    recorder.current = null;
    setMode('idle');
    tone('stop');
    if (!result) return;
    await addCapture({
      appointmentId: stopId,
      kind: 'voice_note',
      blob: result.blob,
      caption: `Débreffage — ${customerName}`,
      // The seconds of *speech*, not of driving. A note recorded across a twenty-minute drive
      // with forty seconds of talking in it costs forty seconds of storage and transcription.
      durationSeconds: result.durationSeconds,
    });
    onClose();
  };

  if (!recordingSupported()) return null;

  return (
    <Sheet open={open} title="Débreffage" onClose={onClose}>
      {mode === 'idle' ? (
        <>
          <p className="quiet">
            L’office reçoit le texte dès la synchronisation. Un travail débreffé à 10 h 20 dans la camionnette est un
            travail sur lequel la coordination agit à 10 h 21.
          </p>

          <BigButton onClick={() => void start('curb')} hint="Soixante secondes, à l’arrêt">
            Dicter maintenant
          </BigButton>

          <BigButton tone="quiet" onClick={() => void start('drive')} hint="Mains libres pour tout le trajet">
            Débreffer en route
          </BigButton>

          <p className="quiet">
            En route : rien à regarder, rien à toucher. Une tonalité au départ, une à l’arrêt. Ne manipulez pas
            l’appareil en conduisant.
          </p>
        </>
      ) : (
        <>
          <p className="reading__value tnum">{elapsed} s</p>
          {unreliable && (
            <p className="warn" role="alert">
              Cet appareil peut interrompre l’enregistrement si l’écran s’éteint. Gardez-le allumé sur son support.
            </p>
          )}
          <p className="quiet">
            {mode === 'drive'
              ? 'En cours. Arrêtez-vous avant de toucher l’appareil.'
              : 'En cours.'}
          </p>
          <BigButton tone="danger" onClick={() => void stop()}>
            Arrêter et enregistrer
          </BigButton>
        </>
      )}
    </Sheet>
  );
}
